import {
  createRef,
  memo,
  MutableRefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import toast, { Toaster } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import PermitDetails from "./PermitDetails";
import useFormWithSchema from "../../../hooks/useFormWithSchema";
import {
  permitToWorkAttachmentKeys,
  PermitToWorkDetails,
  STATUS_IDS,
  USER_ROLES_IDS,
} from "../../../constants/common";
import useAuthStore from "../../../store/Auth";
import { createPermitToWorkSchema } from "../../../schemas/createPermitToWorkSchema";
import { formattedDateTime } from "../../../lib/functions";
import { APP_ROUTES } from "../../../routes";
import { useLocation } from "react-router-dom";
import ScopeOfWorkCard from "../../../components/card/ScopeOfWorkCard";
import Attachments from "./Attachments";
import Undertakings from "./Undertakings";
import SafetyRequirementChecklistCard from "../../../components/card/SafetyRequirementChecklistCard";
import { useGetPermitToWorkById } from "../../../hooks/permit/query";
import Loader from "../../../components/loader";
import Acknowledgement from "./Acknowledgement";
import Header from "../Header";
import ActionButtonGroup from "./ActionButtonGroup";

const PermitToWork = () => {
  const { t } = useTranslation();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const key = searchParams.get("ID");

  const [isAttachments, setIsAttachments] = useState({
    haveWorkPlan: 0,
    haveMethodStatement: 0,
    haveRiskAssessment: 0,
  });

  const [isQualifiedPersonnelOnly, setIsQualifiedPersonnelOnly] =
    useState(false);
  const [isSafetyBriefingConducted, setIsSafetyBriefingConducted] =
    useState(false);
  const [
    isAgreeStopWorkIfConditionsChange,
    setIsAgreeStopWorkIfConditionsChange,
  ] = useState(false);

  const [selectedWorkCategory, setSelectedWorkCategory] = useState<string>("");

  const [checkedBuildingIds, setCheckedBuildingIds] = useState<number[]>([]);

  const { data: permitToWork, isPending: isPermitToWorkLoading } =
    useGetPermitToWorkById({
      encryptedId: key || "",
    });

  const {
    control,
    formState: { errors },
    clearErrors,
    handleSubmit,
    watch,
    setValue,
  } = useFormWithSchema(
    createPermitToWorkSchema(isAttachments, selectedWorkCategory),
    {
      reValidateMode: "onChange",
      defaultValues: {
        permitStartDate:
          new Date() > new Date(new Date().setHours(7, 0, 0, 0))
            ? formattedDateTime(new Date())
            : formattedDateTime(new Date(new Date().setHours(7, 0, 0, 0))),
        permitEndDate: formattedDateTime(
          new Date(
            (new Date() > new Date(new Date().setHours(7, 0, 0, 0))
              ? new Date()
              : new Date(new Date().setHours(7, 0, 0, 0))
            ).getTime() +
            60 * 60 * 1000,
          ),
        ),
      },
    },
  );

  const uploadRefs = useRef<
    Record<string, MutableRefObject<HTMLInputElement | null>>
  >(
    permitToWorkAttachmentKeys.reduce(
      (acc, key) => {
        acc[key] = createRef<HTMLInputElement>();
        return acc;
      },
      {} as Record<string, MutableRefObject<HTMLInputElement | null>>,
    ),
  );

  const [fileNames, setFileNames] = useState<Record<string, string>>(
    permitToWorkAttachmentKeys.reduce(
      (acc, key) => ({ ...acc, [key]: "" }),
      {},
    ),
  );
  const setFileName = (key: string, value: React.SetStateAction<string>) => {
    setFileNames((prev) => ({
      ...prev,
      [key]: typeof value === "function" ? value(prev[key]) : value,
    }));
  };

  const permitStartDate = new Date(watch("permitStartDate") || "");

  const [safetyRequirementsChecklist, setSafetyRequirementsChecklist] =
    useState<Record<string, boolean>>({});

  const { user } = useAuthStore();

  const [permitDetails, setPermitDetails] = useState<PermitToWorkDetails>();

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      toast.error(t("fillAllInfo"));
    }
  }, [errors, t]);

  useEffect(() => {
    if (!permitToWork) {
      setPermitDetails(undefined);

      setValue("affectedService", "");
      setValue("workCategory", "");
      setValue("highRiskWorkCategory", "");
      setValue("scopeOfWork", "");
      setValue("workType", "");
      setValue("floorNo", "");
      setValue("officeNumber", "");

      setValue(
        "permitStartDate",
        new Date() > new Date(new Date().setHours(7, 0, 0, 0))
          ? formattedDateTime(new Date())
          : formattedDateTime(new Date(new Date().setHours(7, 0, 0, 0))),
      );
      setValue(
        "permitEndDate",
        formattedDateTime(
          new Date(
            (new Date() > new Date(new Date().setHours(7, 0, 0, 0))
              ? new Date()
              : new Date(new Date().setHours(7, 0, 0, 0))
            ).getTime() +
            60 * 60 * 1000,
          ),
        ),
      );

      setCheckedBuildingIds([]);

      setIsAttachments({
        haveWorkPlan: 0,
        haveMethodStatement: 0,
        haveRiskAssessment: 0,
      });
      return;
    }

    const permitData = {
      id: permitToWork?.n_PermitToWorkID,
      affectedService: permitToWork?.n_AffectedService?.toString() || "",
      permitStartDate: permitToWork?.dt_PermitStartDate,
      permitEndDate: permitToWork?.dt_PermitEndDate,
      workCategory: permitToWork?.n_WorkCategoryCode?.toString() || "",
      highRiskWorkCategory:
        permitToWork?.n_HighRiskWorkCategoryCode?.toString() || "",
      scopeOfWork: permitToWork?.s_ScopeOfWork || "",
      workType: permitToWork?.n_WorkTypeCode?.toString() || "",
      floorNo: permitToWork?.s_FloorNo || "",
      officeNumber: permitToWork?.s_OfficeNo || "",
    };

    setPermitDetails(permitData);

    Object.entries(permitData).forEach(([key, value]) => {
      setValue(key as any, value);
    });

    const buildings = permitToWork.buildings?.map((building: number) => building) || [];
    setCheckedBuildingIds(buildings);

    setSelectedWorkCategory(permitData.workCategory);
    setIsAttachments({
      haveWorkPlan: permitToWork?.haveWorkPlanFile ? 1 : 0,
      haveMethodStatement: permitToWork?.haveMethodStatementFile ? 1 : 0,
      haveRiskAssessment: permitToWork?.haveRiskAssessmentFile ? 1 : 0,
    });

    setSafetyRequirementsChecklist(
      (permitToWork?.safetyChecklist || []).reduce(
        (acc: any, section: any) => ({
          ...acc,
          ...section.items.reduce(
            (itemAcc: any, item: any) => ({
              ...itemAcc,
              [item.id]: item.checked,
            }),
            {},
          ),
        }),
        {} as Record<string, boolean>,
      ),
    );
  }, [permitToWork, setValue]);

  const isPermitApprovedByNextApprover = useMemo(
    () =>
      !!(
        location.pathname === APP_ROUTES.UPDATE_PERMIT_TO_WORK &&
        permitToWork?.n_CurrentApproverOrderNo &&
        permitToWork?.n_CurrentApproverOrderNo > 1
      ),
    [location.pathname, permitToWork?.n_CurrentApproverOrderNo],
  );

  const isFieldsDisabled = useMemo(
    () =>
      (user?.n_RoleID !== USER_ROLES_IDS.STAFF &&
        user?.n_RoleID !== USER_ROLES_IDS.ADMIN) ||
      (permitToWork && permitToWork?.n_ApprovalStatus !== STATUS_IDS.PENDING) ||
      (user?.n_RoleID === USER_ROLES_IDS.STAFF &&
        isPermitApprovedByNextApprover) ||
      (user?.n_RoleID === USER_ROLES_IDS.ADMIN &&
        location.pathname === APP_ROUTES.APPROVE_PERMIT_TO_WORK),
    [
      isPermitApprovedByNextApprover,
      location.pathname,
      permitToWork,
      user?.n_RoleID,
    ],
  );

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />

      <div className="flex flex-col gap-5">
        <Header title={t("permitToWork")} data={permitToWork} />

        {key && isPermitToWorkLoading ? (
          <div className="flex justify-center">
            <Loader />
          </div>
        ) : (
          <>
            <PermitDetails
              control={control}
              errors={errors}
              clearErrors={clearErrors}
              setValue={setValue}
              permitStartDate={permitStartDate}
              selectedWorkCategory={selectedWorkCategory}
              setSelectedWorkCategory={setSelectedWorkCategory}
              isFieldsDisabled={isFieldsDisabled}
              checkedBuildingIds={checkedBuildingIds}
              setCheckedBuildingIds={setCheckedBuildingIds}
            />

            <ScopeOfWorkCard
              control={control}
              errors={errors}
              isFieldsDisabled={isFieldsDisabled}
            />

            <SafetyRequirementChecklistCard
              safetyRequirementsChecklist={safetyRequirementsChecklist}
              setSafetyRequirementsChecklist={setSafetyRequirementsChecklist}
              isFieldsDisabled={isFieldsDisabled}
            />

            <Attachments
              control={control}
              errors={errors}
              isAttachments={isAttachments}
              uploadRefs={uploadRefs.current}
              fileNames={fileNames}
              setFileName={setFileName}
              permitDetails={permitDetails}
              isFieldsDisabled={isFieldsDisabled}
              selectedWorkCategory={selectedWorkCategory}
            />

            <Undertakings />

            <Acknowledgement
              isQualifiedPersonnelOnly={isQualifiedPersonnelOnly}
              isSafetyBriefingConducted={isSafetyBriefingConducted}
              isAgreeStopWorkIfConditionsChange={isAgreeStopWorkIfConditionsChange}
              setIsQualifiedPersonnelOnly={setIsQualifiedPersonnelOnly}
              setIsSafetyBriefingConducted={setIsSafetyBriefingConducted}
              setIsAgreeStopWorkIfConditionsChange={
                setIsAgreeStopWorkIfConditionsChange
              }
              isFieldsDisabled={isFieldsDisabled}
            />

            <ActionButtonGroup
              permitToWork={permitToWork}
              handleSubmit={handleSubmit}
              checkedBuildingIds={checkedBuildingIds}
              safetyRequirementsChecklist={safetyRequirementsChecklist}
              isQualifiedPersonnelOnly={isQualifiedPersonnelOnly}
              isSafetyBriefingConducted={isSafetyBriefingConducted}
              isAgreeStopWorkIfConditionsChange={isAgreeStopWorkIfConditionsChange}
              isPermitApprovedByNextApprover={isPermitApprovedByNextApprover}
              isFieldsDisabled={isFieldsDisabled}
            />
          </>
        )}
      </div>
    </>
  );
};

export default memo(PermitToWork);
